<template>
  <modal id="stock-car-detail" :title="title" @close="close">
    <loader v-if="isLoading" />
    <div v-else-if="stockCar" class="stock-car-detail">
      <div class="images">
        <img :src="stockCar.Configuration.Images.Exterior" />
        <img :src="stockCar.Configuration.Images.Interior" />
      </div>
      <section>
        <dl class="stock-car-options">
          <dt>{{ i18n.t('/StockDashboard/Detail/BasicCar') }}</dt>
          <dd>{{ stockCar.Configuration.CatalogCarName }}</dd>
          <dd class="stock-car-options--price">
            {{ i18n.n(stockCar.Configuration.NetCatalogPrice.Incl, 'currency') }}
          </dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/Color') }}</dt>
          <dd>{{ stockCar.Configuration.Color.Name }}</dd>
          <dd class="stock-car-options--price">
            {{ i18n.n(stockCar.Configuration.Color.Price.Incl, 'currency') }}
          </dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/Rims') }}</dt>
          <dd>{{ stockCar.Configuration.Rim.Name }}</dd>
          <dd class="stock-car-options--price">
            {{ i18n.n(stockCar.Configuration.Rim.Price.Incl, 'currency') }}
          </dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/Upholstery') }}</dt>
          <dd>{{ stockCar.Configuration.Interior.Upholstery.Name }}</dd>
          <dd class="stock-car-options--price">
            {{ i18n.n(stockCar.Configuration.Interior.Upholstery.Price.Incl, 'currency') }}
          </dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/UpholsteryColor') }}</dt>
          <dd>{{ stockCar.Configuration.Interior.UpholsteryColor.Name }}</dd>
          <dd class="stock-car-options--price">
            {{ i18n.n(stockCar.Configuration.Interior.UpholsteryColor.Price.Incl, 'currency') }}
          </dd>
          <template v-for="(feature, index) in selectedFeatures" :key="index">
            <dt>{{ feature.Id }}</dt>
            <dd>{{ feature.Name }}</dd>
            <dd class="stock-car-options--price">
              {{ feature.Price }}
            </dd>
          </template>
          <dt>{{ i18n.t('/StockDashboard/Detail/TotalPrice') }}</dt>
          <dd></dd>
          <dd class="price">
            {{ i18n.n(stockCar.Configuration.Price.Incl, 'currency') }}
          </dd>
        </dl>
        <dl>
          <dt>{{ i18n.t('/StockDashboard/Detail/LicensePlate') }}</dt>
          <dd>{{ stockCar.Configuration.LicensePlate }}</dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/Vin') }}</dt>
          <dd>{{ stockCar.Configuration.Vin }}</dd>

          <dt>{{ i18n.t('/StockDashboard/Detail/StockTypeLabel') }}</dt>
          <dd>{{ type }}</dd>
          <dt>{{ i18n.t('/StockDashboard/Detail/Location') }}</dt>
          <dd>{{ location }}</dd>

          <dt>
            {{ i18n.t('/StockDashboard/ReservationStatus/IDS/StatusMessage') }}
          </dt>
          <dd>{{ idsStatusMessage }}</dd>
        </dl>
        <div class="stock-car-detail--actions">
          <app-button
            button-class="primary js-track-click"
            data-action="offer-stockcar-to-customer"
            data-qa="stock-car-detail-create-quote-button"
            @click="createQuote"
          >
            {{ i18n.t('/StockDashboard/Detail/MakeQuoteButton') }}
          </app-button>
          <app-button button-class="secondary" data-qa="back-cancel-button" @click="cancel">
            {{ i18n.t('/StockDashboard/Detail/CancelButton') }}
          </app-button>
        </div>
      </section>
    </div>
  </modal>
</template>

<script>
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Configuration_Api from '@/api/server/Configuration'
import Establishment_API from '@/api/server/Establishments.api'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'

export default {
  components: {
    Loader,
    Modal,
    AppButton,
  },
  props: {
    stockCarId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'onCancel', 'onError'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      stockCar: null,
      establishment: null,
      isLoading: false,
    }
  },
  computed: {
    location() {
      if (this.establishment) {
        return `${this.establishment.Name}, ${this.establishment.City}`
      }
      return this.i18n.t('/StockDashboard/Detail/EstablishmentUnknown')
    },
    type() {
      switch (this.stockCar.StockCarDetails.StockType) {
        case 'NATIONAL':
          return 'National' || this.stockCar.StockCarDetails.StockType
        case 'DEALER':
          return 'Dealer' || this.stockCar.StockCarDetails.StockType
        case 'SHOWROOM':
          return 'Showroom' || this.stockCar.StockCarDetails.StockType
        case 'DEMO':
          return 'Demo' || this.stockCar.StockCarDetails.StockType
        default:
          return 'unknown' || this.stockCar.StockCarDetails.StockType
      }
    },
    selectedFeatures() {
      let features = []

      if (this.stockCar.Configuration.Categories) {
        // flatten all features into a single array
        for (let i = 0; i < this.stockCar.Configuration.Categories.length; i++) {
          features = features.concat(
            [],
            this.stockCar.Configuration.Categories[i].Features.map((c) => {
              return {
                Id: c.Id,
                Name: c.Name,
                Price: this.i18n.n(c.Price.Incl, 'currency'),
              }
            })
          )
        }
      }

      return features
    },
    idsStatusMessage() {
      return this.stockCar?.Reservation?.StatusIds?.StatusMessage
    },
  },
  mounted() {
    this.getStockCar()
  },
  methods: {
    async getStockCar() {
      this.isLoading = true

      try {
        this.stockCar = await Configuration_Api.getStockcar(this.stockCarId)
        const establishmentId = this.stockCar.StockCarDetails.Establishment
        /* eslint-disable-next-line */
        this.establishment = await Establishment_API.getEstablishment(establishmentId)
      } catch (error) {
        this.$emit('onError')
      } finally {
        this.isLoading = false
      }
    },
    async createQuote() {
      this.$router.push({
        name: 'new-stockcar-page',
        params: {
          stockCarId: this.stockCar.Id,
          cstType: this.$route.params.cstType,
        },
        query: this.$route.query,
      })
    },
    cancel() {
      this.$emit('onCancel')
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-car-detail {
  display: grid;
  grid-template-rows: auto auto;
  row-gap: 12px;
  .images {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 12px;

    img {
      transition: transform 0.1s;
    }
    img:hover {
      transform: scale(2);
    }
  }

  &--actions {
    display: flex;
    justify-content: space-between;
  }
}

dl {
  display: grid;
  grid-template-columns: 10em auto;
  border-top: 1px solid #dae2e5;
  margin-bottom: 1em;

  & + & {
    border-top: 0;
    margin-top: -1em;
  }

  &.stock-car-options {
    display: grid;
    grid-template-columns: 10em auto auto;
  }
}

dt,
dd {
  padding: 0.5em 0;
  border-bottom: 1px solid #dae2e5;
}

dd {
  &.stock-car-options--price,
  &.price {
    padding-left: 0.5em;
    text-align: right;
  }
}

dt {
  font-weight: $font-weight-bold;
  padding-right: 0.5em;
}

.price {
  font-weight: $font-weight-bold;
}
</style>
